import React, { useState } from 'react'
import styled from 'styled-components'
import { createTheme, ThemeProvider } from '@material-ui/core/styles'
import { Paper, Box, TextField, Button, Grid, Card, Typography } from '@material-ui/core'
import { AppBar, Tabs, Tab } from '@material-ui/core'
import LinkIcon from '@material-ui/icons/Link'
import { useDispatch } from 'react-redux'
import InputBase from '@material-ui/core/InputBase'

import { updateSheet } from '../redux/sheetDbSlice'
import { addSheetInformation, updateSheetInformation } from '../redux/sheetInformationDbSlice';
import useWindowSize from '@rehooks/window-size'
import useCurrentDb from '../hooks/useCurrentDb'
import useCurrentSheetDb from '../hooks/useCurrentSheetDb'
import useSheetInformationDbs from '../hooks/useSheetInformationDbs';
import TabPanel, { a11yProps } from './TabPanel'
import useSheetTreeNamesForLink, { LinkSheetNameType } from '../hooks/useSheetTreeNamesForLink'
import useCurrentBookDb from '../hooks/useCurrentBookDb'
import useSheetIdsForLink from '../hooks/useSheetIdsForLink'
import { setDefaultSheet } from '../redux/sheetDbSlice'

import { WorkAreaStateType } from '../components/BookEditor';

const RowField = styled(InputBase)`
  width: 100%;
`
const SheetInformationPaper = styled(Paper)`
  position: relative;
  overflow: auto;
  padding: 0;
  z-index: 2;
`
const SheetInformationHeaderGrid = styled(Grid)`
  padding: 10px;
`
const SheetInformationBodyGrid = styled(Grid)`
  padding: 10px;
`
const TabsAppBar = styled(AppBar)`
  color: black;
  background-color: white;
`
const WideTable = styled.table`
  width: 100%;
  margin-top: 24px;
`
const BorderTh = styled.th`
  border: solid 1px black;
`
const BorderTd = styled.td`
  border: solid 1px black;
`
const LabelTypography = styled(Typography)`
  color: #9e9e9e;
`
const LineCard = styled(Card)`
  padding: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
  transition: background-color 300ms;
  &:hover {
    background-color: #f5f5f5;
    cursor: pointer;
  }
`
const LargeLinkIcon = styled(LinkIcon)`
  position: relative;
  top: 8px;
  font-size: 32px;
`

const tabsIndicatorTheme = createTheme({
  overrides: {
    MuiTabs: {
      indicator: {
        backgroundColor: '#bdbdbd',
      },
    },
  },
})

type SimpleTabsType = {
  nodeClicked: (workArea: WorkAreaStateType, id: string) => void
}

const SimpleTabs:React.FC<SimpleTabsType> = ({nodeClicked}) => {
  const dispatch = useDispatch();
  const [sheetId] = useCurrentDb();
  const [ids, sheetInformations] = useSheetInformationDbs();
  const [value, setValue] = useState(0);
  const bookDb = useCurrentBookDb();
  const linkSheetIds = useSheetIdsForLink(sheetId); //当該シートにリンクしているカードまたは矢印が存在するシートのIDを配列で返す
  const linkedSheetNames:LinkSheetNameType[] = useSheetTreeNamesForLink(bookDb?.name, linkSheetIds);

  const changeTab = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  }
  const changeArgs = (ev: any) => {
    const newArgs = ev.target.value
    dispatch(updateSheetInformation({id: ids[0], content: newArgs}))
  }
  const changeItemName = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, id: string) => {
    const newItemName = ev.target.value;
    dispatch(updateSheetInformation({id: id, name: newItemName}));
  }
  const changeItemContent = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, id: string) => {
    const newItemContent = ev.target.value;
    dispatch(updateSheetInformation({id: id, content: newItemContent}));
  }
  const addItem = () => {
    dispatch(addSheetInformation({sheetId: sheetId || 'no sheet', name: '', content: '', order: ids.length}));
  }  
  const jumpToLinkedSheet = (sheetId:string) => {
    nodeClicked('sheet', sheetId);
    dispatch(setDefaultSheet(sheetId));
  }

  return (
    <ThemeProvider theme={tabsIndicatorTheme}>
      <TabsAppBar position="static" elevation={0}>
        <Tabs value={value} onChange={changeTab} variant="fullWidth">
          <Tab label="情報" {...a11yProps(0)} />
          <Tab label="リンク元" {...a11yProps(1)} />
          {/* <Tab label="変更履歴" {...a11yProps(2)} /> */}
        </Tabs>
      </TabsAppBar>
      <TabPanel value={value} index={0}>
        <Box paddingTop="24px">
          <form autoComplete="off" onDoubleClick={() => console.log('押された')}>
            <TextField label="概要" variant="outlined" multiline minRows={6} maxRows={6} fullWidth
              value={sheetInformations[0].content} onChange={changeArgs} />
          </form>
          <WideTable>
            <thead>
              <tr>
                <BorderTh>項目名</BorderTh>
                <BorderTh>内容</BorderTh>
              </tr>
            </thead>
            <tbody>
            { ids.map((id, ix) => {
              if (ix === 0) return undefined;
              const sheetInformation = sheetInformations[ix];
              return (
                <tr key={id}>
                  <BorderTd>
                    <RowField 
                      value={sheetInformation.name}
                      onChange={(ev) => changeItemName(ev, id)}
                    />
                  </BorderTd>
                  <BorderTd>
                    <RowField 
                      value={sheetInformation.content}
                      onChange={(ev) => changeItemContent(ev, id)}
                    />
                  </BorderTd>
                </tr>
              );
            }) }
            </tbody>
          </WideTable>
          <Button color='primary' onClick={addItem} variant="outlined">項目追加</Button>
        </Box>
      </TabPanel>
      <TabPanel value={value} index={1}>
        <LabelTypography variant="caption">
          {(linkSheetIds.length > 0)? 'クリックするとリンク元のシートへジャンプします' : 'このシートはリンクされていません'}
        </LabelTypography>
        {linkedSheetNames.map((linkedSheetName, index) => {
          return(
            <LineCard onClick={() => {jumpToLinkedSheet(linkSheetIds[index])}}>
              <Grid container spacing={1}>
                <Grid item><LargeLinkIcon /></Grid>
                <Grid item>
                  <Grid container>
                    <Grid item>
                      <Typography variant="caption">{linkedSheetName.linkTreeName}</Typography>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item>
                      <Typography variant="body1">{linkedSheetName.sheetName}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
                </Grid>
            </LineCard>
        )})}
      </TabPanel>
      {/* <TabPanel value={value} index={2}>
      </TabPanel> */}
    </ThemeProvider>
  );
}

type SheetInformationProps = {
  completionClicked: () => void
  nodeClicked: (workArea: WorkAreaStateType, id?: string) => void
}
const SheetInformation: React.FC<SheetInformationProps> = ({completionClicked, nodeClicked}) => {
  const dispatch = useDispatch()
  const windowSize = useWindowSize()
  const workHeight = windowSize.innerHeight - 64
  const [sheetId] = useCurrentDb()
  const currentSheet = useCurrentSheetDb()

  const changeSheetName = (ev: any) => {
    const newSheetName = ev.target.value
    if (sheetId) {
      dispatch(updateSheet({id: sheetId, name: newSheetName}))
    }
  }
  const fillBlank = (ev: any) => {
    if (!ev.target.value && sheetId) dispatch(updateSheet({id: sheetId, name: 'blank'}))
  }

  return (
    <SheetInformationPaper style={{height: workHeight}} elevation={0} square>
      <SheetInformationHeaderGrid container alignItems="center" spacing={1}>
        <Grid item xs={8}>
          <TextField variant="outlined" value={currentSheet?.name}
            onChange={changeSheetName} onBlur={fillBlank} fullWidth />
        </Grid>
        <Grid item xs={4}>
          <Button color='primary' onClick={() => completionClicked()} size="large">完了</Button>
        </Grid>
      </SheetInformationHeaderGrid>
      <SheetInformationBodyGrid container justify="center">
        <Grid item xs={11}>
          <SimpleTabs nodeClicked={nodeClicked} />
        </Grid>
      </SheetInformationBodyGrid>
    </SheetInformationPaper>
  )
}

export default SheetInformation
